import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import { StoryButton, WrapStatusScreen, WrapStory, markWrapSeen } from '@/features/wrap';
import useYearWrap from '@/features/wrap/hooks/useYearWrap';

export const Route = createFileRoute('/wrap')({
  component: WrapPage,
});

const LOADING_LINES = [
  'Counting your office days…',
  'Finding the desk you kept coming back to…',
  'Checking who brought the treats…',
  'Lining up your longest streak…',
];
const LOADING_LINE_MS = 1800;

/** Paints the browser's toolbar in the story's color while it's open. */
function useThemeColorOf(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const element = ref.current;
    if (!meta || !element) return;
    const previous = meta.getAttribute('content');
    meta.setAttribute('content', getComputedStyle(element).backgroundColor);
    return () => {
      if (previous) meta.setAttribute('content', previous);
    };
  }, [ref]);
}

function LoadingScreen({ onClose }: { onClose: () => void }) {
  const [line, setLine] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(
      () => setLine(current => (current + 1) % LOADING_LINES.length),
      LOADING_LINE_MS
    );
    return () => window.clearInterval(timer);
  }, []);

  return (
    <WrapStatusScreen
      busy
      title="Putting your year together"
      description={<span aria-hidden>{LOADING_LINES[line]}</span>}
      onClose={onClose}
    />
  );
}

function WrapPage() {
  const { user } = useAuth();
  const { state, retry } = useYearWrap(user);
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = useNavigate();
  const pageRef = useRef<HTMLElement>(null);
  useThemeColorOf(pageRef);

  const close = useCallback(() => {
    if (canGoBack) router.history.back();
    else navigate({ to: '/profile', search: { settings: undefined }, replace: true });
  }, [canGoBack, router, navigate]);

  const readyYear = state.status === 'ready' ? state.wrap.year : null;
  useEffect(() => {
    if (readyYear !== null) markWrapSeen(readyYear);
  }, [readyYear]);

  return (
    <main ref={pageRef} className="fixed inset-0 bg-wrap-night text-wrap-cream">
      {state.status === 'ready' && <WrapStory wrap={state.wrap} onClose={close} />}
      {state.status === 'loading' && <LoadingScreen onClose={close} />}
      {state.status === 'closed' && (
        <WrapStatusScreen
          title="Your year in RobinHoot isn't available right now"
          description="Come back later for your office days, favorite desk, longest streak and more."
          onClose={close}
        >
          <StoryButton tone="night" appearance="solid" onClick={close}>
            Back to RobinHoot
          </StoryButton>
        </WrapStatusScreen>
      )}
      {state.status === 'error' && (
        <WrapStatusScreen
          title="We couldn't put your year together"
          description="Something went wrong while counting. Give it another try in a moment."
          onClose={close}
        >
          <StoryButton tone="night" appearance="solid" onClick={retry}>
            Try again
          </StoryButton>
          <StoryButton tone="night" onClick={close}>
            Back
          </StoryButton>
        </WrapStatusScreen>
      )}
    </main>
  );
}
